// Customer-facing floor plan picker.
//
// Pure SVG — keeps the public widget bundle small (no Konva). Each seat is
// a clickable rect/circle scaled into the plan's logical coordinate system.
// Multi-plan services render tabs across the top (Inside / Outside / …).
//
// Seat state here is "selected" only. True per-booking availability (sold,
// locked, pending payment) arrives in a later PR once the bookings table
// exists; for now every kind=seat element is considered available.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  WidgetFloorPlan,
  WidgetFloorPlanElement,
  WidgetService,
  WidgetUnit,
} from "../types";

export type SeatSelection = {
  plan: WidgetFloorPlan;
  element: WidgetFloorPlanElement;
  unit: WidgetUnit | null;
};

export function FloorPlanPicker({
  service,
  selected,
  onSelect,
  unavailableElementIds,
  activePlanId,
  onActivePlanChange,
}: {
  service: WidgetService;
  selected: SeatSelection | null;
  onSelect: (sel: SeatSelection | null) => void;
  unavailableElementIds?: Set<string>;
  activePlanId?: string | null;
  onActivePlanChange?: (id: string) => void;
}) {
  const plans = service.floor_plans;
  const [internalPlanId, setInternalPlanId] = useState<string | null>(
    plans[0]?.id ?? null,
  );
  const effectivePlanId = activePlanId ?? internalPlanId;

  const activePlan =
    plans.find((p) => p.id === effectivePlanId) ?? plans[0] ?? null;

  if (!activePlan) return null;

  return (
    <div className="vt-plan">
      {plans.length > 1 && (
        <div className="vt-plan-tabs" role="tablist">
          {plans.map((p) => {
            const isActive = p.id === activePlan.id;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`vt-plan-tab${isActive ? " vt-plan-tab-active" : ""}`}
                onClick={() => {
                  if (onActivePlanChange) onActivePlanChange(p.id);
                  else setInternalPlanId(p.id);
                  // Clear selection when switching plans — seat IDs are scoped to a plan.
                  onSelect(null);
                }}
              >
                {p.name}
              </button>
            );
          })}
        </div>
      )}

      <PlanCanvas
        plan={activePlan}
        units={service.units}
        selectedElementId={selected?.element.id ?? null}
        unavailableElementIds={unavailableElementIds}
        onSeatClick={(element, unit) => {
          if (unavailableElementIds?.has(element.id)) return;
          onSelect(
            selected?.element.id === element.id
              ? null
              : { plan: activePlan, element, unit },
          );
        }}
      />

      {selected ? (
        <SelectionSummary selection={selected} />
      ) : (
        <p className="vt-plan-hint">Tap a seat to select it.</p>
      )}
    </div>
  );
}

// ─── Canvas ────────────────────────────────────────────────────────────────

function PlanCanvas({
  plan,
  units,
  selectedElementId,
  unavailableElementIds,
  onSeatClick,
}: {
  plan: WidgetFloorPlan;
  units: WidgetUnit[];
  selectedElementId: string | null;
  unavailableElementIds?: Set<string>;
  onSeatClick: (
    el: WidgetFloorPlanElement,
    unit: WidgetUnit | null,
  ) => void;
}) {
  const unitById = useMemo(
    () => new Map(units.map((u) => [u.id, u])),
    [units],
  );

  const vp = useViewport(plan.width, plan.height);
  const viewBox = `${vp.x} ${vp.y} ${vp.w} ${vp.h}`;

  return (
    <div className="vt-plan-canvas-wrap">
      <svg
        ref={vp.svgRef}
        viewBox={viewBox}
        role="img"
        aria-label={`Floor plan: ${plan.name}`}
        className={`vt-plan-canvas${vp.panning ? " vt-plan-canvas-panning" : ""}`}
        preserveAspectRatio="xMidYMid meet"
        onWheel={vp.onWheel}
        onPointerDown={vp.onPointerDown}
        onPointerMove={vp.onPointerMove}
        onPointerUp={vp.onPointerUp}
        onPointerCancel={vp.onPointerUp}
      >
        {plan.background_image_url && (
          <image
            href={plan.background_image_url}
            x={0}
            y={0}
            width={plan.width}
            height={plan.height}
            preserveAspectRatio="xMidYMid slice"
            opacity={0.5}
          />
        )}
        {/* Decorations, walls, labels render first; seats on top. */}
        {[...plan.elements]
          .sort((a, b) => kindOrder(a.kind) - kindOrder(b.kind))
          .map((el) => {
            if (el.kind !== "seat") return <Decoration key={el.id} el={el} />;
            const unit = el.service_unit_id
              ? unitById.get(el.service_unit_id) ?? null
              : null;
            const isSelected = el.id === selectedElementId;
            const isUnavailable = unavailableElementIds?.has(el.id) ?? false;
            return (
              <Seat
                key={el.id}
                el={el}
                unit={unit}
                selected={isSelected}
                unavailable={isUnavailable}
                onClick={() => onSeatClick(el, unit)}
              />
            );
          })}
      </svg>
      <div className="vt-plan-zoom">
        <button
          type="button"
          onClick={vp.toggleLock}
          aria-label={vp.locked ? "Unlock zoom" : "Lock zoom"}
          aria-pressed={vp.locked}
          title={vp.locked ? "Zoom locked — click to unlock" : "Zoom unlocked — click to lock"}
          className={`vt-plan-zoom-btn vt-plan-zoom-lock${vp.locked ? " vt-plan-zoom-locked" : ""}`}
        >
          {vp.locked ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth={2}>
              <rect x="5" y="11" width="14" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0" />
            </svg>
          )}
        </button>
        <button
          type="button"
          onClick={vp.zoomOut}
          aria-label="Zoom out"
          className="vt-plan-zoom-btn"
        >
          −
        </button>
        <button
          type="button"
          onClick={vp.reset}
          aria-label="Reset view"
          className="vt-plan-zoom-btn vt-plan-zoom-reset"
          disabled={vp.isReset}
        >
          {Math.round((plan.width / vp.w) * 100)}%
        </button>
        <button
          type="button"
          onClick={vp.zoomIn}
          aria-label="Zoom in"
          className="vt-plan-zoom-btn"
        >
          +
        </button>
      </div>
    </div>
  );
}

// ─── Pan + zoom ────────────────────────────────────────────────────────────

type Viewport = {
  svgRef: React.RefObject<SVGSVGElement | null>;
  x: number;
  y: number;
  w: number;
  h: number;
  panning: boolean;
  isReset: boolean;
  // Locked = wheel + pinch zoom suppressed. The explicit +/- zoom
  // buttons and the lock toggle itself still work. Default is locked
  // so a guest scrolling the page doesn't accidentally zoom the plan.
  locked: boolean;
  toggleLock: () => void;
  onWheel: (e: React.WheelEvent<SVGSVGElement>) => void;
  onPointerDown: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp: (e: React.PointerEvent<SVGSVGElement>) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
};

function useViewport(planW: number, planH: number): Viewport {
  const svgRef = useRef<SVGSVGElement>(null);
  const [box, setBox] = useState({ x: 0, y: 0, w: planW, h: planH });
  // Reset whenever the plan dimensions change (switching plans).
  useEffect(() => {
    setBox({ x: 0, y: 0, w: planW, h: planH });
  }, [planW, planH]);

  const activePointers = useRef(new Map<number, { x: number; y: number }>());
  const [panning, setPanning] = useState(false);
  // Used to suppress the trailing click fired by the browser after a pan,
  // so a drag doesn't accidentally toggle a seat selection.
  const movedDuringPan = useRef(false);
  const lastPinchDist = useRef<number | null>(null);
  // Lock starts ON — guests scrolling the page shouldn't accidentally
  // zoom the plan via the wheel, nor pinch-zoom while panning. Unlock
  // toggles via the lock button in the zoom overlay.
  const [locked, setLocked] = useState(true);
  const toggleLock = useCallback(() => setLocked((v) => !v), []);

  const clampBox = useCallback(
    (nx: number, ny: number, nw: number, nh: number) => {
      const minSize = Math.min(planW, planH) * 0.15;
      const maxSize = Math.max(planW, planH) * 2;
      nw = Math.max(minSize, Math.min(maxSize, nw));
      nh = Math.max(minSize, Math.min(maxSize, nh));
      // Keep at least 20% of the plan in view.
      const slackX = planW * 0.8;
      const slackY = planH * 0.8;
      nx = Math.max(-slackX, Math.min(planW + slackX - nw, nx));
      ny = Math.max(-slackY, Math.min(planH + slackY - nh, ny));
      return { x: nx, y: ny, w: nw, h: nh };
    },
    [planW, planH],
  );

  const zoomAt = useCallback(
    (svgX: number, svgY: number, factor: number) => {
      setBox((prev) => {
        const nw = prev.w / factor;
        const nh = prev.h / factor;
        const nx = svgX - (svgX - prev.x) * (nw / prev.w);
        const ny = svgY - (svgY - prev.y) * (nh / prev.h);
        return clampBox(nx, ny, nw, nh);
      });
    },
    [clampBox],
  );

  function clientToSvg(clientX: number, clientY: number) {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const r = svg.getBoundingClientRect();
    return {
      x: box.x + ((clientX - r.left) / r.width) * box.w,
      y: box.y + ((clientY - r.top) / r.height) * box.h,
    };
  }

  const onWheel = useCallback(
    (e: React.WheelEvent<SVGSVGElement>) => {
      // When locked, let the wheel scroll the page instead of zooming.
      // No preventDefault means the native scroll path runs unchanged.
      if (locked) return;
      e.preventDefault();
      const { x, y } = clientToSvg(e.clientX, e.clientY);
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      zoomAt(x, y, factor);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [box.x, box.y, box.w, box.h, zoomAt, locked],
  );

  function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    movedDuringPan.current = false;
    if (activePointers.current.size === 1) {
      (e.target as Element).setPointerCapture?.(e.pointerId);
    }
    if (activePointers.current.size === 2) {
      const [a, b] = [...activePointers.current.values()];
      lastPinchDist.current = Math.hypot(a!.x - b!.x, a!.y - b!.y);
    }
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    const prev = activePointers.current.get(e.pointerId);
    if (!prev) return;

    if (activePointers.current.size === 2) {
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const [a, b] = [...activePointers.current.values()];
      const dist = Math.hypot(a!.x - b!.x, a!.y - b!.y);
      // Locked = no pinch-zoom. Two-finger gesture still records as a
      // pan-suppressor (movedDuringPan) so a stray second-finger touch
      // doesn't accidentally toggle seat selection.
      if (!locked && lastPinchDist.current) {
        const cx = (a!.x + b!.x) / 2;
        const cy = (a!.y + b!.y) / 2;
        const svg = clientToSvg(cx, cy);
        zoomAt(svg.x, svg.y, dist / lastPinchDist.current);
      }
      lastPinchDist.current = dist;
      movedDuringPan.current = true;
      return;
    }

    if (activePointers.current.size === 1) {
      const dx = e.clientX - prev.x;
      const dy = e.clientY - prev.y;
      if (Math.abs(dx) + Math.abs(dy) < 4) return;
      activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      setPanning(true);
      movedDuringPan.current = true;
      setBox((prevBox) => {
        const svg = svgRef.current;
        if (!svg) return prevBox;
        const r = svg.getBoundingClientRect();
        const sx = (dx / r.width) * prevBox.w;
        const sy = (dy / r.height) * prevBox.h;
        return clampBox(prevBox.x - sx, prevBox.y - sy, prevBox.w, prevBox.h);
      });
    }
  }

  function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) lastPinchDist.current = null;
    if (activePointers.current.size === 0) {
      setPanning(false);
      if (movedDuringPan.current) {
        // Eat the subsequent click so we don't toggle a seat on pan release.
        const swallow = (ev: MouseEvent) => {
          ev.stopPropagation();
          ev.preventDefault();
          window.removeEventListener("click", swallow, true);
        };
        window.addEventListener("click", swallow, true);
      }
      movedDuringPan.current = false;
    }
  }

  function zoomIn() {
    setBox((prev) => {
      const cx = prev.x + prev.w / 2;
      const cy = prev.y + prev.h / 2;
      const nw = prev.w / 1.3;
      const nh = prev.h / 1.3;
      return clampBox(cx - nw / 2, cy - nh / 2, nw, nh);
    });
  }

  function zoomOut() {
    setBox((prev) => {
      const cx = prev.x + prev.w / 2;
      const cy = prev.y + prev.h / 2;
      const nw = prev.w * 1.3;
      const nh = prev.h * 1.3;
      return clampBox(cx - nw / 2, cy - nh / 2, nw, nh);
    });
  }

  function reset() {
    setBox({ x: 0, y: 0, w: planW, h: planH });
  }

  const isReset =
    box.x === 0 && box.y === 0 && box.w === planW && box.h === planH;

  return {
    svgRef,
    x: box.x,
    y: box.y,
    w: box.w,
    h: box.h,
    panning,
    isReset,
    locked,
    toggleLock,
    onWheel,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    zoomIn,
    zoomOut,
    reset,
  };
}

// ─── Visuals ───────────────────────────────────────────────────────────────

function Seat({
  el,
  unit,
  selected,
  unavailable,
  onClick,
}: {
  el: WidgetFloorPlanElement;
  unit: WidgetUnit | null;
  selected: boolean;
  unavailable: boolean;
  onClick: () => void;
}) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const transform = `rotate(${el.rotation} ${cx} ${cy})`;
  const fontSize = Math.max(10, Math.min(14, Math.floor(el.width / 7)));

  const title = [
    el.label ?? unit?.name ?? "Seat",
    unit?.base_price != null
      ? `${unit.currency ?? "ZAR"} ${Math.round(Number(unit.base_price))}`
      : null,
  ]
    .filter(Boolean)
    .join(" — ");

  const stateClass = unavailable
    ? " vt-seat-unavailable"
    : selected
      ? " vt-seat-selected"
      : "";

  return (
    <g
      transform={transform}
      className={`vt-seat${stateClass}`}
      onClick={unavailable ? undefined : onClick}
      role="button"
      tabIndex={unavailable ? -1 : 0}
      aria-label={unavailable ? `${title} — unavailable` : title}
      aria-disabled={unavailable}
      aria-pressed={selected && !unavailable}
      onKeyDown={(e) => {
        if (unavailable) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {el.shape === "circle" ? (
        <ellipse
          cx={cx}
          cy={cy}
          rx={el.width / 2}
          ry={el.height / 2}
          className="vt-seat-shape"
        />
      ) : el.shape === "custom" && el.icon_svg ? (
        <foreignObject x={el.x} y={el.y} width={el.width} height={el.height}>
          <span
            className="vt-seat-custom"
            dangerouslySetInnerHTML={{ __html: el.icon_svg }}
          />
        </foreignObject>
      ) : (
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={6}
          ry={6}
          className="vt-seat-shape"
        />
      )}
      <text
        x={cx}
        y={cy}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={fontSize}
        className="vt-seat-label"
      >
        {el.label ?? unit?.name ?? ""}
      </text>
      {(el.capacity ?? unit?.capacity) != null && (
        <text
          x={cx}
          y={el.y + el.height - 4}
          textAnchor="middle"
          fontSize={Math.max(8, fontSize - 3)}
          className="vt-seat-capacity"
        >
          {el.capacity ?? unit?.capacity}
        </text>
      )}
      <title>{title}</title>
    </g>
  );
}

function Decoration({ el }: { el: WidgetFloorPlanElement }) {
  const cx = el.x + el.width / 2;
  const cy = el.y + el.height / 2;
  const transform = `rotate(${el.rotation} ${cx} ${cy})`;

  if (el.kind === "label") {
    const fontSize = Math.max(12, Math.min(28, Math.floor(el.height * 0.45)));
    return (
      <g transform={transform}>
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          className="vt-plan-label"
        >
          {el.label ?? ""}
        </text>
      </g>
    );
  }

  if (el.kind === "wall") {
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          className="vt-plan-wall"
        />
      </g>
    );
  }

  // decoration — render per metadata.type
  const type = (el.metadata as Record<string, unknown>)?.type as
    | string
    | undefined;

  if (type === "tree") {
    const r = Math.max(6, Math.min(el.width, el.height) / 2 - 2);
    return (
      <g transform={transform}>
        <ellipse cx={cx} cy={cy} rx={r} ry={r} className="vt-plan-tree" />
        <ellipse
          cx={cx}
          cy={cy}
          rx={r * 0.55}
          ry={r * 0.55}
          className="vt-plan-tree-core"
        />
      </g>
    );
  }

  if (type === "grass") {
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={4}
          className="vt-plan-grass"
        />
        {el.label && (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            className="vt-plan-grass-label"
          >
            {el.label}
          </text>
        )}
      </g>
    );
  }

  if (type === "play_area") {
    const fontSize = Math.max(11, Math.min(18, Math.floor(el.height * 0.22)));
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={10}
          className="vt-plan-play"
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          className="vt-plan-play-label"
        >
          {el.label || "PLAY"}
        </text>
      </g>
    );
  }

  if (type === "pool") {
    const fontSize = Math.max(11, Math.min(16, Math.floor(el.height * 0.22)));
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={12}
          className="vt-plan-pool"
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={fontSize}
          className="vt-plan-pool-label"
        >
          {el.label || "Pool"}
        </text>
      </g>
    );
  }

  if (type === "planter") {
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={3}
          className="vt-plan-planter"
        />
        <rect
          x={el.x + 3}
          y={el.y + 3}
          width={el.width - 6}
          height={Math.max(4, el.height / 3)}
          rx={2}
          className="vt-plan-planter-top"
        />
      </g>
    );
  }

  if (type === "couch") {
    return (
      <g transform={transform}>
        <rect
          x={el.x}
          y={el.y}
          width={el.width}
          height={el.height}
          rx={8}
          className="vt-plan-couch"
        />
        <rect
          x={el.x + 3}
          y={el.y + 3}
          width={el.width - 6}
          height={Math.max(4, el.height / 4)}
          rx={4}
          className="vt-plan-couch-back"
        />
        {el.label && (
          <text
            x={cx}
            y={cy + el.height * 0.12}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize={11}
            className="vt-plan-couch-label"
          >
            {el.label}
          </text>
        )}
      </g>
    );
  }

  if (type === "bar_stool") {
    const r = Math.max(6, Math.min(el.width, el.height) / 2 - 2);
    return (
      <g transform={transform}>
        <ellipse cx={cx} cy={cy} rx={r} ry={r} className="vt-plan-stool" />
        <ellipse
          cx={cx}
          cy={cy}
          rx={r * 0.6}
          ry={r * 0.6}
          className="vt-plan-stool-seat"
        />
      </g>
    );
  }

  // generic decoration fallback
  return (
    <g transform={transform}>
      <rect
        x={el.x}
        y={el.y}
        width={el.width}
        height={el.height}
        rx={4}
        className="vt-plan-decoration"
      />
      {el.label && (
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={11}
          className="vt-plan-decoration-label"
        >
          {el.label}
        </text>
      )}
    </g>
  );
}

// ─── Selection summary ────────────────────────────────────────────────────

function SelectionSummary({ selection }: { selection: SeatSelection }) {
  const { element, unit } = selection;
  const capacity = element.capacity ?? unit?.capacity ?? null;
  const price =
    unit?.base_price != null
      ? `${unit.currency ?? "ZAR"} ${Number(unit.base_price).toLocaleString()}`
      : null;

  return (
    <div className="vt-plan-summary">
      <div className="vt-plan-summary-main">
        <div className="vt-plan-summary-name">
          {element.label ?? unit?.name ?? "Seat"}
        </div>
        <div className="vt-plan-summary-meta">
          {capacity ? `Up to ${capacity}` : null}
          {capacity && unit?.name && element.label ? " · " : ""}
          {element.label && unit?.name && element.label !== unit.name
            ? unit.name
            : ""}
        </div>
      </div>
      {price && <div className="vt-plan-summary-price">{price}</div>}
    </div>
  );
}

function kindOrder(kind: string): number {
  if (kind === "decoration") return 0;
  if (kind === "wall") return 1;
  if (kind === "label") return 2;
  return 3; // seat (top)
}
